<template>
  <div>
    <section class="hero-slab colour-hero">
      <div class="hero-slab__media" :style="{ backgroundImage: 'url(' + page.heroImage + ')' }"></div>
      <div class="hero-slab__overlay"></div>
      <div class="hero-slab__content container reveal">
        <div class="colour-hero__content">
          <p class="colour-hero__lead">{{ page.lead }}</p>
          <h1>NATURALLY <strong>COLOURS</strong></h1>
          <div class="text-stack colour-hero__text">
            <p>{{ page.intro }}</p>
            <p v-for="item in page.notes" :key="item">{{ item }}</p>
          </div>
        </div>
      </div>
    </section>

    <page-trail
      :items="[
        { label: $t('common.home'), to: '/' },
        { label: $t('nav.products'), to: '/products' },
        { label: $t('nav.colours'), to: '/colours' }
      ]"
      :back-label="$t('common.backToProducts')"
      back-to="/products"
    />

    <section class="section-spacing light-panel colour-entry-section">
      <div class="container reveal">
        <div class="colour-entry-grid">
          <router-link
            v-for="item in page.families"
            :key="item.title"
            :to="'/products-list/' + item.slug"
            class="colour-entry-card"
          >
            <div class="colour-entry-card__image">
              <img :src="item.image" :alt="item.title" loading="lazy">
            </div>
            <h3>{{ item.title }}</h3>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import site from '../mock/strongova'
import PageTrail from '../components/PageTrail.vue'
import { fetchPublicCategories, fetchPublicPageSections, resolveAssetUrl } from '../api/public'

export default {
  name: 'ColoursPage',
  components: {
    PageTrail
  },
  data () {
    return {
      page: site.coloursPage,
      pageSections: []
    }
  },
  created () {
    Promise.all([
      fetchPublicCategories({ type: 'colour' }),
      fetchPublicPageSections({ pageKey: 'colours' })
    ])
      .then(([categories, sections]) => {
        const page = { ...site.coloursPage }
        const intro = sections.find(item => item.sectionKey === 'intro')

        if (intro) {
          page.title = intro.title || page.title
          page.intro = intro.description || page.intro
          page.heroImage = resolveAssetUrl(intro.image, page.heroImage)
        }

        if (Array.isArray(categories) && categories.length) {
          page.families = categories.map(item => this.normalizeColourFamily(item))
        }

        this.page = page
        this.pageSections = sections
      })
      .catch(() => {
        this.page = site.coloursPage
        this.pageSections = []
      })
  },
  methods: {
    normalizeColourFamily (item) {
      const fallback = site.coloursPage.families.find(family => family.slug === item.slug) || {}
      return {
        title: item.name,
        slug: item.slug,
        image: resolveAssetUrl(item.image, fallback.image || site.coloursPage.heroImage),
        body: item.description || fallback.body || '',
        href: `/products-list/${item.slug}`,
        tone: fallback.tone || '',
        bestFor: fallback.bestFor || ''
      }
    }
  }
}
</script>

<style>
.colour-hero {
  min-height: 68vh;
}

.colour-hero .hero-slab__content {
  min-height: calc(68vh - 60px);
  padding-bottom: 56px;
}

.colour-hero .hero-slab__overlay {
  background:
    linear-gradient(90deg, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0.34) 48%, rgba(0, 0, 0, 0.12) 100%),
    linear-gradient(180deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0.66) 100%);
}

.colour-hero__content {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(360px, 0.66fr);
  gap: clamp(40px, 6vw, 96px);
  align-items: end;
  width: 100%;
}

.colour-hero__lead {
  margin: 0 0 18px;
  max-width: 560px;
  font-size: 15px;
  line-height: 1.5;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(245, 243, 239, 0.72);
}

.colour-hero h1 {
  margin: 0;
  font-size: clamp(44px, 5vw, 78px);
  line-height: 1.02;
  font-weight: 400;
  text-transform: uppercase;
  max-width: 720px;
  color: #f5f3ef;
}

.colour-hero h1 strong {
  font-weight: 400;
}

.colour-hero__text {
  position: relative;
  margin: 0;
  max-width: none;
  padding: 26px 30px 28px;
  border-left: 1px solid rgba(245, 243, 239, 0.3);
  background: rgba(12, 12, 12, 0.36);
  color: rgba(245, 243, 239, 0.76);
  backdrop-filter: blur(6px);
}

.colour-hero__text p {
  margin: 0;
  max-width: none;
  font-size: 16px;
  line-height: 1.76;
}

.colour-hero__text p + p {
  margin-top: 14px;
}

.colour-entry-section {
  padding-top: 56px;
  padding-bottom: 118px;
}

.colour-entry-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(24px, 3vw, 42px);
}

.colour-entry-card {
  display: block;
  position: relative;
}

.colour-entry-card__image {
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: #eee8df;
}

.colour-entry-card__image img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}

.colour-entry-card:hover .colour-entry-card__image img {
  transform: scale(1.06);
}

.colour-entry-card h3 {
  margin: 14px 0 14px;
  padding: 0 14px;
  font-size: clamp(23px, 2.1vw, 32px);
  line-height: 1.2;
  font-weight: 400;
}

.colour-entry-card h3::before {
  display: inline-block;
  width: 36px;
  height: 1px;
  margin-right: 14px;
  vertical-align: middle;
  content: "";
  background: currentColor;
}

@media (max-width: 1100px) {
  .colour-hero__content,
  .colour-entry-grid {
    grid-template-columns: 1fr;
  }

  .colour-hero {
    min-height: 740px;
  }

  .colour-hero .hero-slab__content {
    min-height: calc(740px - 60px);
  }
}

@media (max-width: 640px) {
  .colour-hero {
    min-height: 760px;
  }

  .colour-hero .hero-slab__content {
    min-height: calc(760px - 60px);
    padding-bottom: 36px;
  }

  .colour-hero h1 {
    font-size: 44px;
  }

  .colour-hero__lead {
    font-size: 13px;
  }

  .colour-hero__text {
    padding: 22px 22px 24px;
  }

  .colour-hero__text p {
    font-size: 15px;
    line-height: 1.68;
  }

  .colour-entry-section {
    padding-top: 34px;
    padding-bottom: 78px;
  }

  .colour-entry-card h3::before {
    width: 26px;
    margin-right: 10px;
  }
}
</style>
